import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import LeftSideMenu from '../LeftSideMenu';
import { Formik, ErrorMessage } from 'formik';
import { Input, Form as AntdForm, AutoComplete, notification } from 'antd';
import * as Yup from 'yup';
import { Button } from '@ui/components';
import { connect } from 'react-redux';
import { metaSelectors } from '@ui/store/meta';
import { transformFormatSource } from '@ui/tools/transformSource';
import {
  createRegion,
  ICountry,
  IRegion,
  updateRegion,
} from '@ui/api/v2/regions.api';

const { Item } = AntdForm;

const initialValues = {
  name: '',
  country: '',
};

interface IProps {
  region?: IRegion;
  countries: ICountry[];
  onRegionUpdate: (region: IRegion) => void;
  onRegionCreate: (region: IRegion) => void;
  resetEditRegion: () => void;
}

const regionSchema = Yup.object().shape({
  name: Yup.string().required('Поле обязательно к заполнению'),
  country: Yup.string().required('Поле обязательно к заполнению'),
});

function RegionForm({
  countries,
  region,
  onRegionCreate,
  onRegionUpdate,
  resetEditRegion,
}: IProps) {
  const formikRef = useRef<any>(null);

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      if (region && region.id) {
        resetEditRegion();
        const response = await updateRegion(region.id, values);
        if (response.data) {
          onRegionUpdate(response.data);
        }
      } else {
        const { data, error } = await createRegion(values);
        if (data) {
          onRegionCreate(data);
        } else {
          notification.error({
            message: error.response.data.message,
          });
        }
      }
      resetForm();
    },
    [region, onRegionCreate, onRegionUpdate, resetEditRegion],
  );

  const cancelEditForm = useCallback(
    (_, { resetForm }) => {
      resetForm();
      resetEditRegion();
    },
    [resetEditRegion],
  );

  const dataSource = useMemo(() => transformFormatSource(countries), [
    countries,
  ]);

  useEffect(() => {
    if (formikRef.current && region) {
      formikRef.current.setValues({
        name: region.name,
        country: region.country.id.toString(),
      });
    }
  }, [region]);

  const title = useMemo(() => {
    if (region && region.id) {
      return 'Редактировать область';
    }
    return 'Новая область';
  }, [region]);

  return (
    <LeftSideMenu className="regions__left-side" topPanel={title}>
      <Formik
        ref={formikRef}
        initialValues={initialValues}
        onSubmit={onSubmit}
        onReset={cancelEditForm}
        validationSchema={regionSchema}
      >
        {({
          handleChange,
          handleBlur,
          handleSubmit,
          values,
          setFieldValue,
          handleReset,
        }) => (
          <form onSubmit={handleSubmit} className="regions__form">
            <Item label="Название">
              <Input
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.name}
                size="large"
                type="text"
                name="name"
              />
              <ErrorMessage
                className="regions__form__error"
                name="name"
                component="div"
              />
            </Item>
            <Item label="Страна">
              <AutoComplete
                dataSource={dataSource}
                value={values.country}
                size="large"
                onSelect={value => {
                  setFieldValue('country', value);
                }}
              />
              <ErrorMessage
                name="region"
                className="regions__form__error"
                component="div"
              />
            </Item>
            <div className="regions__buttons-wrapper">
              <Item>
                <Button type="primary" htmlType="submit">
                  Сохранить
                </Button>
              </Item>
              <Item>
                <Button onClick={handleReset}>Отмена</Button>
              </Item>
            </div>
          </form>
        )}
      </Formik>
    </LeftSideMenu>
  );
}

export default connect(state => ({
  countries: metaSelectors.getCountries(state),
}))(memo(RegionForm));



// WEBPACK FOOTER //
// ./src/components/Regions/RegionForm.tsx